import React, { useEffect, useMemo, useState } from 'react';
import {
  createMeeting, updateMeeting, reasonOf, toInstant, toFields, nextHalfHour, lengthOf, ZONE, keyOf, PLATFORMS
} from '../../lib/meetings';
import { chatPeople } from '../../lib/sessionLink';
import { Dialog, GrowingTextarea, Avatar, isContractor, ItTag } from '../todos/TodoPeople';
import { Icon } from '../todos/icons';
import RecurrencePicker, { cleanRecurrence } from './Recurrence';
import TodosModeChoice from './TodosMode';

/*
 * MAKING A MEETING, or changing one (Gordon, 22 October).
 *
 * One dialog, top to bottom in the order somebody thinks about it: what it
 * is about, where (online, or a room in the office), when and for how long,
 * who - NCMB staff and contractors from the chat directory, and outside
 * guests by email, who will get an .ics invitation with a link that asks
 * only for their name - how often, and then the three decisions that are
 * the organiser's alone: a waiting room for guests (on, unless turned off),
 * whether the minutes are reviewed first or sent automatically, and whether
 * the to-dos go into one shared list or a private list each.
 *
 * Dates and times are typed as Malta's (lib/meetings, toInstant), 24-hour,
 * and sent as instants with the zone named. Length is a choice rather than
 * an end time, because "an hour" is how a meeting is thought of; the end is
 * worked out and shown.
 *
 * Editing one meeting of a series asks, on Save, whether the change is for
 * this one, this and the following ones, or all of them - the question
 * every calendar asks, asked once, at the moment it matters.
 */

const LENGTHS = [15, 30, 45, 60, 90, 120, 180, 240];
const addMinutes = (date, time, minutes) => {
  const iso = toInstant(date, time);
  if (!iso) return null;
  return new Date(new Date(iso).getTime() + minutes * 60000).toISOString();
};
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fromMeeting = (m) => {
  const start = toFields(m.starts_at);
  const mins = Math.max(5, Math.round((new Date(m.ends_at) - new Date(m.starts_at)) / 60000) || 60);
  return {
    title: m.title || '',
    agenda: m.agenda || '',
    kind: m.kind === 'room' ? 'room' : m.kind === 'call' ? 'call' : 'online',
    platform: m.platform || '',
    location: m.location || '',
    date: start.date,
    time: start.time,
    length: mins,
    // A guest with no email (090) is somebody who was there, by name.
    attendees: (m.attendees || []).filter((a) => a.role !== 'organiser').map((a) => (a.kind === 'guest'
      ? (a.email ? { kind: 'guest', email: a.email, name: a.name || '' } : { kind: 'guest', name: a.name || '' })
      : { kind: a.kind === 'contractor' ? 'contractor' : 'user', key: a.key, name: a.name })),
    recurrence: cleanRecurrence(m.recurrence),
    lobby: m.lobby !== false,
    minutes_mode: m.minutes_mode === 'auto' ? 'auto' : 'review',
    send_minutes: Boolean(m.send_minutes),
    todos_mode: m.todos_mode === 'personal' ? 'personal' : 'shared'
  };
};

const fresh = () => {
  const { date, time } = nextHalfHour();
  return {
    title: '', agenda: '', kind: 'online', platform: '', location: '', date, time, length: 60, attendees: [],
    recurrence: null, lobby: true, minutes_mode: 'review', send_minutes: false, todos_mode: 'shared'
  };
};

export default function MeetingForm({ meeting = null, onSaved, onClose }) {
  const editing = Boolean(meeting?.id);
  const [f, setF] = useState(() => (meeting ? fromMeeting(meeting) : fresh()));
  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));
  const [people, setPeople] = useState(null);
  const [q, setQ] = useState('');
  const [guest, setGuest] = useState(null); // { email, name } while the guest row is open
  const [byName, setByName] = useState(''); // a guest by name only (090), on a meeting nobody joins by link
  // A meeting that has started (an ad-hoc one most of all) keeps its kind:
  // the server refuses a change, and the radio would promise one.
  const kindFixed = editing && meeting.state && meeting.state !== 'scheduled';
  const adhoc = Boolean(meeting?.adhoc);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [scopeAsk, setScopeAsk] = useState(false);
  const [scope, setScope] = useState('this');

  useEffect(() => {
    let live = true;
    chatPeople().then((rows) => { if (live) setPeople(rows); }).catch(() => { if (live) setPeople([]); });
    return () => { live = false; };
  }, []);

  const idOf = (a) => a.key || (a.email ? `g:${String(a.email).toLowerCase()}` : `n:${String(a.name || '').trim().toLowerCase()}`);
  const chosen = useMemo(() => new Set(f.attendees.map(idOf)), [f.attendees]);
  const hits = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    return (people || []).map((p) => ({ ...p, key: keyOf(p) }))
      .filter((p) => p.key && p.name && !chosen.has(p.key) && String(p.name).toLowerCase().includes(t))
      .slice(0, 8);
  }, [q, people, chosen]);

  const addPerson = (p) => {
    set({ attendees: [...f.attendees, { kind: isContractor(p) ? 'contractor' : 'user', key: p.key, name: p.name }] });
    setQ('');
  };
  const addGuest = () => {
    const email = String(guest?.email || '').trim().toLowerCase();
    if (!EMAIL.test(email)) { setError('That does not look like an email address.'); return; }
    if (chosen.has(`g:${email}`)) { setGuest(null); return; }
    set({ attendees: [...f.attendees, { kind: 'guest', email, name: String(guest?.name || '').trim() }] });
    setGuest(null);
    setError(null);
  };
  const addByName = () => {
    const n = byName.trim().slice(0, 120);
    if (!n) return;
    if (!chosen.has(`n:${n.toLowerCase()}`)) set({ attendees: [...f.attendees, { kind: 'guest', name: n }] });
    setByName('');
  };
  const remove = (i) => set({ attendees: f.attendees.filter((_, j) => j !== i) });

  const endsAt = addMinutes(f.date, f.time, Number(f.length) || 60);
  const startsAt = toInstant(f.date, f.time);
  const lengthChoices = LENGTHS.includes(Number(f.length)) ? LENGTHS : [...LENGTHS, Number(f.length)].sort((a, b) => a - b);

  const body = () => ({
    title: f.title.trim(),
    agenda: f.agenda.trim() || null,
    kind: f.kind,
    ...(f.kind === 'call' ? { platform: f.platform || null } : {}),
    starts_at: startsAt,
    ends_at: endsAt,
    timezone: ZONE,
    location: f.kind === 'room' ? (f.location.trim() || null) : null,
    attendees: f.attendees.map((a) => (a.kind === 'guest'
      ? (a.email ? { kind: 'guest', email: a.email, ...(a.name ? { name: a.name } : {}) } : { kind: 'guest', name: a.name })
      : { kind: a.kind, key: a.key })),
    recurrence: cleanRecurrence(f.recurrence),
    lobby: f.lobby,
    minutes_mode: f.minutes_mode,
    send_minutes: f.send_minutes,
    todos_mode: f.todos_mode
  });

  const problems = () => {
    if (!f.title.trim()) return 'Give the meeting a title.';
    if (!startsAt) return 'Pick a date and a time.';
    // A meeting started on the spot was wherever the phone was; the room can stay unsaid.
    if (f.kind === 'room' && !f.location.trim() && !adhoc) return 'Say which room.';
    if (f.kind === 'online' && f.attendees.some((a) => a.kind === 'guest' && !a.email)) return 'A guest by name only cannot join an online meeting - give their email, or make it a meeting in the room.';
    return null;
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const p = problems();
    if (p) { setError(p); return; }
    if (editing && meeting.series_id && !scopeAsk) { setScopeAsk(true); return; }
    setBusy(true);
    setError(null);
    try {
      const saved = editing ? await updateMeeting(meeting.id, body(), meeting.series_id ? scope : 'this')
        : await createMeeting(body());
      onSaved(saved);
    } catch (err) {
      setScopeAsk(false);
      setError(reasonOf(err, editing ? 'Could not save the meeting.' : 'Could not make the meeting.'));
    } finally {
      setBusy(false);
    }
  };

  const endWords = endsAt ? `${toFields(endsAt).time}` : '';

  return (
    <Dialog label={editing ? 'Change the meeting' : 'New meeting'} onClose={onClose} className="tds-panel mt-form-dialog">
      <form className="mt-form" onSubmit={submit} noValidate>
        {error && <p className="mt-error" role="alert">{error}</p>}

        <label className="tds-field">
          <span>Title</span>
          <input type="text" value={f.title} maxLength={200} autoFocus placeholder="e.g. Q4 accounts review with Borg & Sons"
            aria-label="Title" onChange={(e) => set({ title: e.target.value })} />
        </label>

        <label className="tds-field">
          <span>Agenda (optional)</span>
          <GrowingTextarea rows={3} maxLength={4000} value={f.agenda} aria-label="Agenda"
            placeholder={'One point per line\n1. Where the audit has got to\n2. Open questions on the ledger'}
            onChange={(e) => set({ agenda: e.target.value })} />
        </label>

        <div className="mt-kind" role="radiogroup" aria-label="Where">
          <label className={`mt-kind-opt${f.kind === 'online' ? ' is-on' : ''}${kindFixed ? ' is-fixed' : ''}`}>
            <input type="radio" name="mt-kind" value="online" checked={f.kind === 'online'} disabled={kindFixed} onChange={() => set({ kind: 'online' })} />
            <Icon name="link" size={15} /><span>Online</span>
          </label>
          <label className={`mt-kind-opt${f.kind === 'room' ? ' is-on' : ''}${kindFixed ? ' is-fixed' : ''}`}>
            <input type="radio" name="mt-kind" value="room" checked={f.kind === 'room'} disabled={kindFixed} onChange={() => set({ kind: 'room' })} />
            <Icon name="home" size={15} /><span>In the room</span>
          </label>
          {/* A call on another app (090): planned here, recorded from the
              organiser's browser when it happens. */}
          <label className={`mt-kind-opt${f.kind === 'call' ? ' is-on' : ''}${kindFixed ? ' is-fixed' : ''}`}>
            <input type="radio" name="mt-kind" value="call" checked={f.kind === 'call'} disabled={kindFixed} onChange={() => set({ kind: 'call' })} />
            <Icon name="share" size={15} /><span>Call on another app</span>
          </label>
        </div>
        {f.kind === 'room' && (
          <label className="tds-field">
            <span>Where{adhoc ? ' (optional)' : ''}</span>
            <input type="text" value={f.location} maxLength={200} placeholder="e.g. Boardroom, 2nd floor"
              aria-label="Location" onChange={(e) => set({ location: e.target.value })} />
          </label>
        )}
        {f.kind === 'call' && (
          <label className="tds-field">
            <span>Which app</span>
            <select value={f.platform} aria-label="Which app the call is on" onChange={(e) => set({ platform: e.target.value })}>
              <option value="">Not said</option>
              {PLATFORMS.map(([k, w]) => <option key={k} value={k}>{w}</option>)}
            </select>
          </label>
        )}

        <div className="mt-when">
          <label className="tds-field">
            <span>Date</span>
            <input type="date" value={f.date} aria-label="Date" onChange={(e) => set({ date: e.target.value })} />
          </label>
          <label className="tds-field">
            <span>Start</span>
            <input type="time" value={f.time} step={300} aria-label="Start time" onChange={(e) => set({ time: e.target.value })} />
          </label>
          <label className="tds-field">
            <span>Length</span>
            <select value={String(f.length)} aria-label="Length" onChange={(e) => set({ length: Number(e.target.value) })}>
              {lengthChoices.map((m) => (
                <option key={m} value={String(m)}>{lengthOf(0, m * 60000)}</option>
              ))}
            </select>
          </label>
          <span className="mt-when-ends" data-ends={endsAt || ''}>
            {endWords ? `until ${endWords} (Malta time)` : 'Malta time'}
          </span>
        </div>

        <fieldset className="mt-fieldset">
          <legend>Who</legend>
          {f.attendees.length > 0 && (
            <ul className="mt-chips" aria-label="Attendees">
              {f.attendees.map((a, i) => (
                <li key={idOf(a)} className={`mt-person-chip${a.kind === 'guest' ? ' is-guest' : ''}`}
                  data-attendee={a.key || a.email || a.name}>
                  <Avatar name={a.name || a.email} who={a.key || a.email || a.name} />
                  <span className="mt-person-chip-name">{a.name || a.email}</span>
                  {a.kind === 'contractor' && <ItTag />}
                  {a.kind === 'guest' && <span className="mt-guest-tag" title={a.email || 'By name only - not in Jarvis'}>{a.email ? 'guest' : 'by name'}</span>}
                  <button type="button" className="mt-chip-x" aria-label={`Remove ${a.name || a.email}`} onClick={() => remove(i)}>
                    <Icon name="close" size={12} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-pick">
            <input type="search" value={q} aria-label="Search NCMB staff and contractors" placeholder="Add NCMB staff or a contractor…"
              onChange={(e) => setQ(e.target.value)} />
            {q.trim() && (
              people === null ? <p className="mt-quiet">Loading…</p>
                : hits.length === 0 ? <p className="mt-quiet">Nobody by that name.</p>
                  : (
                    <ul className="mt-pick-list">
                      {hits.map((p) => (
                        <li key={p.key}>
                          <button type="button" className="mt-pick-item" data-person={p.key} onClick={() => addPerson(p)}>
                            <Avatar name={p.name} who={p.key} />
                            <span>{p.name}</span>
                            {isContractor(p) && <ItTag />}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )
            )}
          </div>
          {guest ? (
            <div className="mt-guest-row">
              <label className="tds-field">
                <span>Guest&rsquo;s email</span>
                <input type="email" value={guest.email} autoFocus placeholder="name@client.com" aria-label="Guest email"
                  onChange={(e) => setGuest({ ...guest, email: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }} />
              </label>
              <label className="tds-field">
                <span>Name (optional)</span>
                <input type="text" value={guest.name} placeholder="So the invitation says hello" aria-label="Guest name"
                  onChange={(e) => setGuest({ ...guest, name: e.target.value })}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addGuest(); } }} />
              </label>
              <div className="mt-guest-row-btns">
                <button type="button" className="btn btn-primary tds-btn is-small mt-guest-add" onClick={addGuest}>Add guest</button>
                <button type="button" className="tds-link" onClick={() => setGuest(null)}>Cancel</button>
              </div>
            </div>
          ) : (
            <button type="button" className="tds-link mt-add-guest" onClick={() => setGuest({ email: '', name: '' })}>
              <Icon name="plus" size={14} /> Add an outside guest by email
            </button>
          )}
          {f.kind !== 'online' && (
            // In the room or on a call nobody joins by link, so somebody can
            // be on the list by name alone: "Paul from the auditors".
            <div className="mt-byname">
              <input type="text" value={byName} maxLength={120} placeholder="Or somebody by name only, e.g. Paul from the auditors"
                aria-label="Add somebody by name" autoComplete="off"
                onChange={(e) => setByName(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addByName(); } }} />
              <button type="button" className="btn btn-outline tds-btn is-small mt-byname-add" disabled={!byName.trim()} onClick={addByName}>Add</button>
            </div>
          )}
          <p className="mt-quiet">
            {adhoc ? 'Nobody is emailed about a meeting started on the spot: the list says who was there.'
              : <>Everybody with an address gets a calendar invitation. Outside guests join from its link with their name only{f.lobby ? ', and wait to be let in' : ''}.</>}
          </p>
        </fieldset>

        <RecurrencePicker value={f.recurrence} onChange={(r) => set({ recurrence: r })} anchor={f.date} />

        <fieldset className="mt-fieldset">
          <legend>Options</legend>
          <label className="mt-check">
            <input type="checkbox" checked={f.lobby} onChange={(e) => set({ lobby: e.target.checked })} />
            <span>
              <strong>Waiting room for guests</strong>
              <small>Outside guests wait until you, or anybody from NCMB in the meeting, lets them in.</small>
            </span>
          </label>

          <div className="mt-opt" role="radiogroup" aria-label="Minutes">
            <span className="mt-opt-label">Minutes</span>
            <label className="mt-radio">
              <input type="radio" name="mt-minutes" value="review" checked={f.minutes_mode === 'review'} onChange={() => set({ minutes_mode: 'review' })} />
              <span><strong>I review them first</strong><small>Written as a draft for you to read and approve.</small></span>
            </label>
            <label className="mt-radio">
              <input type="radio" name="mt-minutes" value="auto" checked={f.minutes_mode === 'auto'} onChange={() => set({ minutes_mode: 'auto' })} />
              <span><strong>Send automatically</strong><small>Sent to the attendees as soon as they are written.</small></span>
            </label>
          </div>

          <label className="mt-check">
            <input type="checkbox" checked={f.send_minutes} onChange={(e) => set({ send_minutes: e.target.checked })} />
            <span>
              <strong>Also email minutes to outside guests</strong>
              <small>Otherwise minutes stay inside Jarvis, for NCMB and contractors only.</small>
            </span>
          </label>

          <div className="mt-opt">
            <span className="mt-opt-label">To-dos from this meeting</span>
            <TodosModeChoice value={f.todos_mode} onChange={(v) => set({ todos_mode: v })} name="mt-todos" />
          </div>
        </fieldset>

        <div className="tds-dialog-foot">
          <button type="button" className="btn btn-outline tds-btn" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary tds-btn mt-save-meeting" disabled={busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Make the meeting'}
          </button>
        </div>
      </form>

      {scopeAsk && (
        <Dialog label="Which meetings?" onClose={() => setScopeAsk(false)} className="mt-scope-dialog">
          <p className="tds-quiet">This is one of a series. Change:</p>
          <div className="mt-scope" role="radiogroup" aria-label="Which meetings to change">
            {[['this', 'This meeting only'], ['following', 'This and the following ones'], ['all', 'All meetings in the series']].map(([k, w]) => (
              <label key={k} className={`mt-scope-opt${scope === k ? ' is-on' : ''}`}>
                <input type="radio" name="mt-scope" value={k} checked={scope === k} onChange={() => setScope(k)} />
                <span>{w}</span>
              </label>
            ))}
          </div>
          <div className="tds-dialog-foot">
            <button type="button" className="btn btn-outline tds-btn" disabled={busy} onClick={() => setScopeAsk(false)}>Back</button>
            <button type="button" className="btn btn-primary tds-btn mt-scope-save" disabled={busy} onClick={() => submit()}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Dialog>
      )}
    </Dialog>
  );
}
